/**
 * Trucker Login Page (Iteration 12 — N9)
 * 
 * Mobile-optimized login (same Cognito pool, different UI).
 * Large touch targets (minHeight: 56px, exceeds 44px WCAG floor).
 * 
 * LAT-24: Security-hardened error messages:
 *   - Wrong password AND unknown email: identical "Invalid email or password"
 *   - Forgot-password: identical for known/unknown
 *   - Revoked credential: "Your access has ended. Contact your administrator."
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useAppDispatch } from '../../store/hooks';
import { signIn } from '../../store/slices/authSlice';
import ForgotPasswordForm from '../elements/ForgotPasswordForm';

const TruckerLoginPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;

    setLoading(true);
    setError(null);

    try {
      await dispatch(signIn({ email: email.trim(), password })).unwrap();
      navigate('/trucker/count');
    } catch (err) {
      // LAT-24: Security-hardened error messages
      const errMsg = err?.message || err || '';

      if (errMsg.includes('disabled') || errMsg.includes('revoked')) {
        setError('Your access has ended. Contact your administrator.');
      } else {
        // Same message for wrong password AND unknown email
        setError('Invalid email or password');
      }
    } finally {
      setLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <Box sx={{ maxWidth: 400, mx: 'auto', mt: 8, px: 3 }}>
        <Paper elevation={0} sx={{ p: 4, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Reset Password
          </Typography>
          <ForgotPasswordForm onBack={() => setShowForgotPassword(false)} />
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', mt: 8, px: 3 }}>
      <Paper elevation={0} sx={{ p: 4, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          Trucker Login
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
        >
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            variant="outlined"
            autoComplete="email"
            sx={{
              '& .MuiInputBase-root': {
                minHeight: theme.spacing(14), // 56px — exceeds 44px WCAG floor
              },
            }}
          />

          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
            variant="outlined"
            autoComplete="current-password"
            sx={{
              '& .MuiInputBase-root': {
                minHeight: theme.spacing(14), // 56px
              },
            }}
          />

          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{
              minHeight: theme.spacing(14), // 56px
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
          </Button>

          <Button
            onClick={() => setShowForgotPassword(true)}
            sx={{
              textTransform: 'none',
              color: theme.palette.text.secondary,
            }}
          >
            Forgot password?
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default TruckerLoginPage;
